// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Lớp gọi API của Z-Meet.
//
// ⚠️ Hai quy tắc chi phối toàn bộ file này, và cả hai đều đến từ ADR:
//
//  1. **Phân nhánh theo `code`, KHÔNG theo `message`** (ADR-0004 §3). `message`
//     đổi theo ngôn ngữ và đổi mỗi khi câu chữ được cải thiện; so sánh nó là
//     viết một client hỏng ở lần đầu ai đó sửa dấu chấm câu.
//  2. **Access token sống trong BỘ NHỚ, không localStorage** (ADR-0003 §6.1).
//     Refresh token nằm trong cookie httpOnly mà JavaScript không chạm tới, nên
//     một lỗ hổng XSS lấy được tối đa 10 phút truy cập chứ không phải quyền
//     truy cập dài hạn.

export type Result<T = unknown> = {
  ok: boolean
  status: number
  /** Mã máy đọc được. Đây là thứ duy nhất được phép phân nhánh theo. */
  code: string
  /** Câu để HIỂN THỊ. Đã dịch sẵn theo ngôn ngữ đang chọn. */
  message: string
  data: T
}

export type Account = { id: string; email: string; name: string }
export type Workspace = { id: string; slug: string; name: string; role: string; user_id: string }

let accessToken: string | null = null
let account: Account | null = null
let locale = 'en'

export const session = {
  get token() {
    return accessToken
  },
  get account() {
    return account
  },
  get signedIn() {
    return accessToken !== null
  },
}

export function setLocale(tag: string) {
  locale = tag
}

type Options = {
  method?: string
  body?: unknown
  /** Workspace id gửi kèm. `null` để không gửi header nào. */
  workspace?: string | null
  retry?: boolean
}

/**
 * call gọi API và tự làm mới access token ĐÚNG MỘT lần khi gặp `auth.token_expired`.
 *
 * Không lặp: nếu lần thứ hai vẫn 401 thì phiên đã chết thật, và thử lại mãi chỉ
 * biến một lỗi hiển thị được thành một vòng lặp im lặng.
 */
export async function call<T = unknown>(path: string, opts: Options = {}): Promise<Result<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Gửi ngôn ngữ ĐANG hiển thị thay vì để server đoán từ Accept-Language:
    // người vừa chọn tiếng Việt trên máy cài tiếng Anh phải nhận lỗi tiếng Việt.
    'X-ZMeet-Locale': locale,
  }
  if (accessToken) headers['Authorization'] = 'Bearer ' + accessToken
  if (opts.workspace) headers['X-ZMeet-Workspace'] = opts.workspace

  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  })

  const text = await res.text()
  const data = text ? (JSON.parse(text) as Record<string, unknown>) : {}
  const code = typeof data['code'] === 'string' ? data['code'] : ''
  const message = typeof data['message'] === 'string' ? data['message'] : ''

  if (res.status === 401 && code === 'auth.token_expired' && opts.retry !== false) {
    if (await refresh()) return call<T>(path, { ...opts, retry: false })
  }

  return { ok: res.ok, status: res.status, code, message, data: data as T }
}

/** refresh xoay vòng refresh token bằng cookie httpOnly. */
export async function refresh(): Promise<boolean> {
  const res = await fetch('/v1/auth/refresh', { method: 'POST' })
  if (!res.ok) {
    accessToken = null
    account = null
    return false
  }
  const d = (await res.json()) as { access_token: string; account: Account }
  accessToken = d.access_token
  account = d.account
  return true
}

// ---------------------------------------------------------------------------
// Xác thực
// ---------------------------------------------------------------------------

export type LoginOutcome =
  | { kind: 'signed_in'; account: Account }
  | { kind: 'mfa_required'; token: string; expiresIn: number; message: string }
  | { kind: 'unverified'; message: string }
  | { kind: 'failed'; message: string }

export async function signIn(email: string, password: string): Promise<LoginOutcome> {
  const r = await call<{
    access_token: string
    account: Account
    mfa_required?: boolean
    mfa_token?: string
    expires_in?: number
  }>('/v1/auth/login', { method: 'POST', workspace: null, body: { email, password } })

  // 202 + mfa_required: mật khẩu đúng nhưng chưa đủ. KHÔNG có token ở đây, và
  // cũng chưa có cookie refresh — phiếu chờ là thứ duy nhất người dùng cầm.
  if (r.status === 202 && r.data.mfa_required) {
    return {
      kind: 'mfa_required',
      token: r.data.mfa_token ?? '',
      expiresIn: r.data.expires_in ?? 0,
      message: r.message,
    }
  }
  // Nhánh riêng, KHÔNG gộp vào "sai thông tin đăng nhập" (ADR-0005 §4): nó chỉ
  // xảy ra sau khi mật khẩu đã đúng, nên nó không tiết lộ gì mới.
  if (r.code === 'auth.email_unverified') return { kind: 'unverified', message: r.message }
  if (!r.ok) return { kind: 'failed', message: r.message }

  accessToken = r.data.access_token
  account = r.data.account
  return { kind: 'signed_in', account: r.data.account }
}

export async function completeMFA(mfaToken: string, code: string): Promise<LoginOutcome> {
  const r = await call<{ access_token: string; account: Account }>('/v1/auth/mfa', {
    method: 'POST',
    workspace: null,
    body: { mfa_token: mfaToken, code },
  })
  if (!r.ok) return { kind: 'failed', message: r.message }
  accessToken = r.data.access_token
  account = r.data.account
  return { kind: 'signed_in', account: r.data.account }
}

/**
 * register LUÔN trả về 202 với cùng một thân, dù email đã có tài khoản hay chưa
 * (ADR-0005 §3). Client không được cố suy ra điều gì từ phản hồi — người dùng
 * thật biết chuyện gì xảy ra qua bức thư, và đó là toàn bộ cơ chế.
 */
export async function register(email: string, name: string, password: string) {
  return call('/v1/auth/register', {
    method: 'POST',
    workspace: null,
    body: { email, name, password },
  })
}

export async function signOut() {
  await fetch('/v1/auth/logout', { method: 'POST' })
  accessToken = null
  account = null
}

export async function me() {
  return call<{ account: Account; workspaces: Workspace[] }>('/v1/auth/me', { workspace: null })
}

// ---------------------------------------------------------------------------
// Vào phòng
// ---------------------------------------------------------------------------

export type JoinResponse = {
  outcome: 'admitted' | 'waiting' | 'rejected'
  token?: string
  livekit_url?: string
  session_id?: string
  participant_id?: string
  guest_id?: string
  principal_ref?: string
  /** CHỈ có mặt khi người vào phòng là thành viên workspace. Khách không nhận. */
  workspace_id?: string
  role?: string
  meeting_title?: string
  message?: string
  code?: string

  /**
   * Chế độ ghi hình của TRIỂN KHAI này — do người vận hành đặt, không phải
   * người dùng chọn (ZMEET_RECORDING_MODE).
   *
   *	local    mọi người tự ghi về máy mình, không tốn gì của máy chủ
   *	server   chỉ host, egress ghi vào kho object của máy chủ
   *
   * Vắng mặt thì coi như 'local' — cùng hướng mặc định với server: một client
   * cũ nói chuyện với server mới không được phép vẽ ra một nút tốn tiền.
   */
  recording_mode?: RecordingMode

  /**
   * Phòng đã khoá hay chưa, TẠI THỜI ĐIỂM vào.
   *
   * Kênh sự kiện một mình không đủ: `session.locked` chỉ phát lúc CHUYỂN trạng
   * thái, nên ai vào sau lần khoá — kể cả chính host vừa mất kết nối rồi vào
   * lại — không bao giờ nhận được nó, và nút Khoá của họ hiện như phòng còn mở.
   */
  locked?: boolean
}

export type RecordingMode = 'local' | 'server'

const GUEST_KEY = 'zmeet_guest'

/**
 * Vé tham dự do một tích hợp phát, đọc từ FRAGMENT của URL (ADR-0007 §4.1).
 *
 * Fragment chứ không query string: `#t=…` không đi lên server, nên nó không nằm
 * lại trong access log của proxy nào trên đường — mà một vé trong log là một
 * danh tính vào được phòng nằm trong log.
 *
 * Đọc rồi XOÁ khỏi thanh địa chỉ, nhưng CẤT vào sessionStorage.
 *
 * Hai việc đó phục vụ hai mục đích ngược nhau và cần cả hai:
 *
 *   xoá khỏi URL  — người dùng sao chép thanh địa chỉ gửi cho đồng nghiệp thì
 *                   không kèm theo danh tính của chính mình.
 *   cất lại       — rớt mạng rồi nạp lại trang thì vẫn còn vé để trình. Không
 *                   cất thì một lần F5 là mất phòng, và đó là thứ chỉ xảy ra
 *                   với đúng người đang có mạng tệ nhất.
 *
 * sessionStorage chứ không localStorage: vé chết theo tab. Một cái tab đóng lại
 * là hết chuyện, không để lại danh tính nằm trên máy.
 */
const TICKET_KEY = 'zmeet_ticket'

function takeTicket(): string | undefined {
  const m = /(?:^|&)t=([^&]+)/.exec(location.hash.slice(1))
  if (m) {
    const t = decodeURIComponent(m[1]!)
    history.replaceState(null, '', location.pathname + location.search)
    try {
      sessionStorage.setItem(TICKET_KEY, t)
    } catch {
      // Trình duyệt chặn storage: vé vẫn dùng được lần này, chỉ là không sống
      // qua lần nạp lại. Thà vậy còn hơn không vào được phòng.
    }
    return t
  }
  try {
    return sessionStorage.getItem(TICKET_KEY) ?? undefined
  } catch {
    return undefined
  }
}

/**
 * Token do hệ thống ngoài TỰ KÝ, hình dạng tương thích Jitsi (ADR-0008).
 *
 * Đọc từ hai chỗ, và thứ tự ưu tiên là một lời khuyên có thật:
 *
 *   `#jwt=…`  FRAGMENT — không đi lên server, không vào access log. NÊN DÙNG.
 *   `?jwt=…`  QUERY — đây là cách Jitsi làm, nên nó phải chạy được để một tích
 *             hợp cũ đổi tên miền là xong. Nhưng query string đi lên server và
 *             nằm lại trong log của mọi proxy trên đường, nên token dán vào đó
 *             coi như đã lộ ở mức hạ tầng.
 *
 * Cả hai đều bị xoá khỏi thanh địa chỉ ngay sau khi đọc và cất vào
 * sessionStorage — cùng lý lẽ với vé tham dự ở trên.
 */
const JWT_KEY = 'zmeet_jwt'

function takeAppToken(): string | undefined {
  const fromHash = /(?:^|&)jwt=([^&]+)/.exec(location.hash.slice(1))
  const fromQuery = new URLSearchParams(location.search).get('jwt')
  const raw = fromHash ? decodeURIComponent(fromHash[1]!) : fromQuery
  if (raw) {
    const url = new URL(location.href)
    url.searchParams.delete('jwt')
    url.hash = ''
    history.replaceState(null, '', url.pathname + url.search)
    try {
      sessionStorage.setItem(JWT_KEY, raw)
    } catch {
      // Trình duyệt chặn storage: token vẫn dùng được lần này, chỉ là không
      // sống qua lần nạp lại.
    }
    return raw
  }
  try {
    return sessionStorage.getItem(JWT_KEY) ?? undefined
  } catch {
    return undefined
  }
}

// Đọc MỘT LẦN lúc nạp trang, không phải lúc gọi join: takeTicket xoá fragment,
// và người dùng thường phải gõ tên rồi bấm nút trước khi join chạy — lúc đó
// fragment đã bị các lần render dọn mất.
const ticket = takeTicket()
const appToken = takeAppToken()

/** Có token của hệ thống ngoài hay không — màn hình vào phòng dùng để bỏ qua
 *  bước hỏi tên khi token đã nói tên rồi. */
export function hasAppToken(): boolean {
  return !!appToken
}

export type JoinTarget = { code?: string; room?: string }

export async function join(target: JoinTarget, passcode: string, displayName: string) {
  const r = await call<JoinResponse>('/v1/join', {
    method: 'POST',
    workspace: null,
    body: {
      join_code: target.code || undefined,
      room: target.room || undefined,
      passcode: passcode || undefined,
      display_name: displayName || undefined,
      // Guest id chứng minh TÍNH LIÊN TỤC của một cái tên trong một session,
      // không phải danh tính (ADR-0003 §2.5). Gửi lại để vào lại đúng chỗ cũ
      // thay vì bị coi là người mới.
      guest_id: sessionStorage.getItem(GUEST_KEY) ?? undefined,
      ticket,
      jwt: appToken,
    },
  })
  if (r.data.guest_id) sessionStorage.setItem(GUEST_KEY, r.data.guest_id)
  return r
}

export type ParticipantStatus = {
  state: string
  role: string
  token?: string
  livekit_url?: string
  /**
   * Chỉ có khi state = 'removed'. `true` nghĩa là bị MỜI RA — gõ cửa lại được,
   * và host sẽ phải bấm Duyệt lần nữa. `false` nghĩa là bị CHẶN hẳn.
   */
  rejoinable?: boolean
}

/**
 * status là đường DUY NHẤT client lấy token sau khi được duyệt.
 *
 * Sự kiện realtime chỉ nói "đi hỏi lại đi" — không mang token (ADR-0006 §5).
 * Token sống 60 giây còn một stream mở hàng giờ, nên đẩy token qua kênh đó thì
 * nó hết hạn trước khi dùng.
 */
export async function participantStatus(sessionID: string, participantID: string, as: string) {
  return call<ParticipantStatus>(
    `/v1/sessions/${sessionID}/participants/${participantID}/status?as=${encodeURIComponent(as)}`,
    { workspace: null },
  )
}

export type MeetingInvite = {
  title?: string
  join_url?: string
  join_code?: string
  passcode?: string
  shareable: boolean
  /**
   * Người được mời sẽ gặp gì: vào thẳng, dừng ở phòng chờ, hay không vào được
   * vì chính sách đòi host có mặt mà phòng chưa có host nào.
   */
  entry?: 'direct' | 'lobby' | 'blocked'
  opened?: boolean
  /**
   * Bấm "mở" sẽ làm gì: mở cuộc họp ra cho người ngoài (`widen`), hay chỉ đặt
   * passcode cho một cuộc họp vốn đã mở nhưng chưa có (`add_passcode`). Hai
   * việc rất khác nhau nấp sau cùng một cái nút.
   */
  open_effect?: 'widen' | 'add_passcode'
  /** Có mặt khi chính sách workspace không cho mở — kèm `message` để hiển thị. */
  code?: string
  message?: string
}

/**
 * meetingInvite lấy thông tin mời, và MỞ lối vào bằng passcode khi `open`.
 *
 * Xác thực bằng cặp (participant_id, principal) như `participantStatus`, không
 * bằng access token: người bấm nút mời thường không có tài khoản Z-Meet — với
 * cuộc gọi mở từ một hệ thống ngoài thì cả phòng đều là khách.
 *
 * `open` mặc định false vì nó ĐỔI chính sách của cuộc họp. Xem thì không đổi
 * gì; đổi thì phải là do người dùng bấm.
 */
export function meetingInvite(
  sessionID: string,
  participantID: string,
  as: string,
  open = false,
) {
  return call<MeetingInvite>(`/v1/sessions/${sessionID}/invite`, {
    method: 'POST',
    workspace: null,
    body: { participant_id: participantID, as, open },
  })
}

// ---------------------------------------------------------------------------
// Điều khiển của host
// ---------------------------------------------------------------------------

/**
 * RoomActor là cách người gọi CHỨNG MINH mình là ai cho một thao tác điều hành.
 *
 *	{ workspace }   thành viên đã đăng nhập — access token + workspace
 *	{ as, actor }   khách — cặp (principal_ref, participant_id) nhận lúc vào phòng
 *
 * Hai đường vì một cuộc họp mở từ hệ thống ngoài KHÔNG có thành viên workspace
 * nào tham dự: mọi người vào bằng vé đều là khách, và khách không có access
 * token nào để xuất trình. Server cưỡng chế cùng một luật phân quyền cho cả hai
 * (xem meetingActor) — đây chỉ là hai cách nói "tôi là ai", không phải hai mức
 * quyền.
 */
export type RoomActor = { workspace: string } | { as: string; actor: string }

/** actorParams tách RoomActor thành header workspace và query của khách. */
function actorParams(a: RoomActor): { workspace: string | null; query: string } {
  if ('workspace' in a) return { workspace: a.workspace, query: '' }
  // Khách gửi bằng chứng qua query. Không dùng body: `listWaiting` là GET, và
  // một cơ chế xác thực chỉ chạy với POST là cơ chế sẽ bị quên ở endpoint tiếp theo.
  return {
    workspace: null,
    query: `?as=${encodeURIComponent(a.as)}&actor=${encodeURIComponent(a.actor)}`,
  }
}

export type Waiting = {
  participant_id: string
  display_name: string
  requested_at: string
  /**
   * Người này đã từng bị mời ra khỏi phòng và đang xin vào lại.
   *
   * Host cần biết TRƯỚC khi bấm Duyệt: trong một cuộc họp đông, cùng một cái
   * tên gõ cửa lần thứ hai trông hệt lần thứ nhất — mà lần thứ hai mới là lần
   * họ vừa bị đuổi ra.
   */
  was_removed?: boolean
}

export function listWaiting(sessionID: string, actor: RoomActor) {
  const a = actorParams(actor)
  return call<{ waiting: Waiting[] }>(`/v1/sessions/${sessionID}/waiting${a.query}`, {
    workspace: a.workspace,
  })
}

export function admit(sessionID: string, participantID: string, actor: RoomActor) {
  const a = actorParams(actor)
  return call(`/v1/sessions/${sessionID}/participants/${participantID}/admit${a.query}`, {
    method: 'POST',
    workspace: a.workspace,
  })
}

/**
 * kick đuổi một người ra khỏi phòng.
 *
 * `permanent` là ranh giới giữa hai việc rất khác nhau:
 *
 *	false  MỜI RA. Họ vào lại được, nhưng rơi thẳng vào phòng chờ — bất kể
 *	       cuộc họp có bật phòng chờ hay không — và host bấm Duyệt lần nữa.
 *	true   CHẶN. Từ chối ngay ở cửa, hết session này.
 *
 * Mặc định là mức nhẹ vì phần lớn lần bấm nút này không phải một bản án: người
 * vào nhầm phòng, người quên tắt micro. Mặc định vĩnh viễn biến mọi lần bấm
 * nhầm thành thứ chỉ sửa được bằng cách kết thúc cuộc họp của tất cả mọi người.
 */
export function kick(
  sessionID: string,
  participantID: string,
  actor: RoomActor,
  reason = '',
  permanent = false,
) {
  const a = actorParams(actor)
  return call(`/v1/sessions/${sessionID}/participants/${participantID}/kick${a.query}`, {
    method: 'POST',
    workspace: a.workspace,
    body: { reason, permanent },
  })
}

/**
 * deny từ chối một người đang Ở PHÒNG CHỜ. Không ghi lệnh cấm — họ gõ cửa lại
 * được. Muốn họ không quay lại nữa thì đó là `kick(..., permanent = true)`.
 */
export function deny(sessionID: string, participantID: string, actor: RoomActor) {
  const a = actorParams(actor)
  return call(`/v1/sessions/${sessionID}/participants/${participantID}/deny${a.query}`, {
    method: 'POST',
    workspace: a.workspace,
  })
}

/**
 * lock khoá phòng: không ai mới vào được nữa, kể cả khi họ biết mã và passcode.
 * Người đang ở trong phòng không bị ảnh hưởng.
 *
 * Bình phương — khoá một phòng đã khoá là 204, không phải lỗi. Hai host cùng
 * bấm không ai nhận lỗi.
 */
export function lock(sessionID: string, actor: RoomActor) {
  const a = actorParams(actor)
  return call(`/v1/sessions/${sessionID}/lock${a.query}`, {
    method: 'POST',
    workspace: a.workspace,
  })
}

/**
 * unlock mở khoá phòng.
 *
 * Endpoint riêng chứ không phải `lock` với một cờ: cả hai đều bình phương, nên
 * hai người cùng bấm cùng một nút cho ra cùng một kết quả. Một endpoint "đảo
 * trạng thái" thì hai lời gọi sẽ triệt tiêu nhau.
 */
export function unlock(sessionID: string, actor: RoomActor) {
  const a = actorParams(actor)
  return call(`/v1/sessions/${sessionID}/unlock${a.query}`, {
    method: 'POST',
    workspace: a.workspace,
  })
}

/**
 * endSession kết thúc cuộc họp cho TẤT CẢ mọi người. Không phải rời phòng.
 *
 * Không cần dọn dẹp gì ở chỗ gọi: server phát `session.ended` trên kênh sự
 * kiện, và mọi client trong phòng — kể cả client vừa bấm nút — đã xử lý sự kiện
 * ấy bằng cách rời phòng và chuyển sang màn hình kết thúc. Tự dọn thêm ở đây là
 * đường thứ hai cho cùng một việc, và một ngày nào đó hai đường sẽ lệch nhau.
 *
 * Với cuộc họp mở từ một hệ thống ngoài, đây cũng là thứ duy nhất báo cho hệ
 * thống đó biết cuộc họp đã tàn (xem internal/store/callback.go). Thiếu nó, cái
 * biển "đang có cuộc gọi" bên kia sống tới khi phòng thực sự rỗng quá thời gian
 * ân hạn — hoặc tới giới hạn thời lượng, nếu còn ai quên đóng tab.
 */
export function endSession(sessionID: string, actor: RoomActor) {
  const a = actorParams(actor)
  return call(`/v1/sessions/${sessionID}/end${a.query}`, {
    method: 'POST',
    workspace: a.workspace,
  })
}

export function startRecording(sessionID: string, workspace: string) {
  return call(`/v1/sessions/${sessionID}/recording/start`, { method: 'POST', workspace })
}

export function stopRecording(sessionID: string, workspace: string) {
  return call(`/v1/sessions/${sessionID}/recording/stop`, { method: 'POST', workspace })
}

export function ackRecording(sessionID: string, workspace: string) {
  return call(`/v1/sessions/${sessionID}/recording/ack`, { method: 'POST', workspace })
}

export type ShareLink = {
  recording_id: string
  /** Địa chỉ đem đi chia sẻ. Mở ra một trang hỏi passcode — xem screens/Recording.tsx. */
  url: string
  /**
   * Cuộc họp có passcode hay không.
   *
   * KHÔNG có thì link kia sẽ từ chối mọi người, và host phải biết điều đó
   * TRƯỚC khi gửi nó cho hai mươi người — mật khẩu của link chính là passcode.
   */
  passcode_set: boolean
  size_bytes?: number | null
  duration_sec?: number | null
  expires_at?: string | null
}

/** recordingShareLink lấy link chia sẻ của bản ghi vừa xong. Host/cohost. */
export function recordingShareLink(sessionID: string, actor: RoomActor) {
  const a = actorParams(actor)
  return call<ShareLink>(`/v1/sessions/${sessionID}/recording/share${a.query}`, {
    workspace: a.workspace,
  })
}

export type ShareDownload = {
  /** URL đã ký, hạn ngắn. Không lưu lại: nó hết hạn sau `expires_in` giây. */
  url: string
  expires_in: number
  title: string
  size_bytes?: number | null
  duration_sec?: number | null
}

/**
 * shareRecording đổi passcode cuộc họp lấy một URL tải.
 *
 * KHÔNG cần đăng nhập — người dự họp phần lớn là khách, và bắt họ tạo tài khoản
 * để xem lại buổi họp họ vừa dự là dựng bức tường ngay chỗ tính năng này sinh
 * ra để gỡ. Vì vậy `workspace: null` là bắt buộc: gửi kèm header workspace của
 * một tab đang đăng nhập sẽ khiến một endpoint công khai đi qua middleware
 * không dành cho nó.
 */
export function shareRecording(recordingID: string, passcode: string) {
  return call<ShareDownload>(`/v1/share/recordings/${encodeURIComponent(recordingID)}`, {
    method: 'POST',
    workspace: null,
    body: { passcode },
  })
}

// ---------------------------------------------------------------------------
// Kênh sự kiện (ADR-0006)
// ---------------------------------------------------------------------------

/** streamTicket xin vé 60 giây cho THÀNH VIÊN mở kênh sự kiện. */
export function streamTicket(sessionID: string, workspace: string) {
  return call<{ ticket: string; expires_in: number }>(`/v1/sessions/${sessionID}/events/ticket`, {
    method: 'POST',
    workspace,
  })
}

export function verifyEmail(token: string) {
  return call<{ email: string }>('/v1/auth/verify-email', {
    method: 'POST',
    workspace: null,
    body: { token },
  })
}

export function resendVerification(email: string) {
  return call('/v1/auth/verify-email/resend', { method: 'POST', workspace: null, body: { email } })
}

// ---------------------------------------------------------------------------
// Điều hành từng người
// ---------------------------------------------------------------------------

export type TrackKind = 'audio' | 'video' | 'screen'

// 'screen' đã được server hỗ trợ từ đầu (media.TrackScreen) nhưng giao diện
// chưa có nút nào gọi tới — một host không dừng được màn hình đang chiếu thứ
// không nên chiếu, và đó là đúng tình huống cần dừng gấp nhất.

/** mute tắt mic/camera của một người NGAY. Họ tự bật lại được. */
export function muteParticipant(
  sessionID: string,
  participantID: string,
  actor: RoomActor,
  track: TrackKind,
) {
  const a = actorParams(actor)
  return call(`/v1/sessions/${sessionID}/participants/${participantID}/mute${a.query}`, {
    method: 'POST',
    workspace: a.workspace,
    body: { track },
  })
}

/**
 * setPublish tước hoặc trả lại QUYỀN phát.
 *
 * Khác mute: bị tước thì họ không tự bật lại được. Công cụ cho một cuộc họp
 * đang bị phá, không phải để nhắc ai đó tắt mic.
 */
export function setPublish(
  sessionID: string,
  participantID: string,
  actor: RoomActor,
  allow: boolean,
) {
  const a = actorParams(actor)
  return call(`/v1/sessions/${sessionID}/participants/${participantID}/publish${a.query}`, {
    method: 'POST',
    workspace: a.workspace,
    body: { allow },
  })
}

/**
 * askUnmute MỜI một người bật lại mic/camera — không bật hộ họ.
 *
 * Host bật micro của người khác từ xa là khả năng giám sát, không phải tính
 * năng điều hành. Lời mời đi qua kênh sự kiện và dừng ở giao diện của họ.
 */
export function askUnmute(
  sessionID: string,
  participantID: string,
  actor: RoomActor,
  track: TrackKind,
) {
  const a = actorParams(actor)
  return call(`/v1/sessions/${sessionID}/participants/${participantID}/ask-unmute${a.query}`, {
    method: 'POST',
    workspace: a.workspace,
    body: { track },
  })
}

export function muteAll(sessionID: string, actor: RoomActor) {
  const a = actorParams(actor)
  return call(`/v1/sessions/${sessionID}/mute-all${a.query}`, {
    method: 'POST',
    workspace: a.workspace,
  })
}

/**
 * ParticipantMeta đến từ METADATA của LiveKit, không phải từ một endpoint danh sách.
 *
 * ⚠️ Không thêm `GET /sessions/{id}/participants` trả về cả participant_id lẫn
 * principal_ref: LiveKit `identity` CHÍNH LÀ principal ref, nên một danh sách
 * như vậy công bố đúng cặp mà `GET .../status` nhận làm bằng chứng — và endpoint
 * đó trả về TOKEN. Với khách, cặp đó là toàn bộ thứ cần để mạo danh họ.
 */
export type ParticipantMeta = { pid: string; role: string }

export function parseParticipantMeta(raw: string | undefined): ParticipantMeta | null {
  if (!raw) return null
  try {
    const m = JSON.parse(raw) as ParticipantMeta
    return typeof m?.pid === 'string' ? m : null
  } catch {
    return null
  }
}
